import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { apiErrorStatus } from './apiErrorStatus.js';

/**
 * One item's detail for the profile slide-out — a trait (CT-5715), an insight
 * (CT-5716). Each kind passes its own request and mapper; the rules live here
 * once:
 *
 * - A race guard (`fetchIdRef`): an answer for an item that is no longer the one
 *   asked for is dropped.
 * - A 404 (the link outlived the item) and a 403 (the viewer may not open it) are
 *   their own outcomes, kept apart from a failure that a retry might fix.
 * - `refresh` re-reads quietly after a write (a report): no skeleton, and a
 *   failed re-read keeps what is on screen.
 *
 * @param {object} options
 * @param {string|null} options.id — nothing is asked without one.
 * @param {() => Promise<{ body: object }>} options.load — the request; stable (`useCallback`).
 * @param {(raw: object) => object} options.map — API → UI; a module-level function.
 * @param {string} options.errorMessage — when the error carries none.
 */
export function useItemDetail({ id, load, map, errorMessage } = {}) {
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const fetchIdRef = useRef(0);

  const fetchData = useCallback(
    async ({ quiet = false } = {}) => {
      if (!id) return;
      const requestId = ++fetchIdRef.current;
      if (!quiet) {
        setLoading(true);
        setError(null);
        setStatus(null);
      }

      try {
        const { body } = await load();
        if (requestId !== fetchIdRef.current) return;
        setItem(body ? map(body) : null);
      } catch (err) {
        if (requestId !== fetchIdRef.current || quiet) return;
        setItem(null);
        setStatus(apiErrorStatus(err));
        setError(err?.message ?? errorMessage);
      } finally {
        if (requestId === fetchIdRef.current) setLoading(false);
      }
    },
    [id, load, map, errorMessage],
  );

  const refetch = useCallback(() => fetchData(), [fetchData]);
  const refresh = useCallback(() => fetchData({ quiet: true }), [fetchData]);

  useEffect(() => {
    fetchData();
    return () => {
      fetchIdRef.current += 1;
    };
  }, [fetchData]);

  return useMemo(
    () => ({
      item,
      loading,
      error,
      notFound: status === 404,
      accessDenied: status === 403,
      refetch,
      refresh,
    }),
    [item, loading, error, status, refetch, refresh],
  );
}

export default useItemDetail;
